import { env } from '@skin-seoul/env-utils';
import CryptoJS from 'crypto-js';

import axios, { AxiosInstance } from 'axios';
import { attachServerLoggingInterceptor } from './loggingInterceptor';
import { getServiceLogger } from './serviceLogger';

const DEFAULT_EVENT_URL = 'https://dev-event.skin-seoul.com';

let hasWarnedMissingEnv = false;

const getEventBaseURL = (): string => {
  const url = env('NEXT_PUBLIC_ENV_EVENT_ORIGIN');
  if (!url && process.env.NODE_ENV !== 'development' && !hasWarnedMissingEnv) {
    hasWarnedMissingEnv = true;
    getServiceLogger().warn(
      '[analyticsClient] NEXT_PUBLIC_ENV_EVENT_ORIGIN is not set, falling back to dev URL',
    );
  }
  return url || DEFAULT_EVENT_URL;
};

const getHmacSecret = (): string | undefined => {
  return env('NEXT_PUBLIC_ANALYTICS_HMAC_SECRET') || process.env.EXPO_PUBLIC_ANALYTICS_HMAC_SECRET || undefined;
};

const signRequest = async (
  body: string,
  secretOverride?: string,
): Promise<string | undefined> => {
  const secret = secretOverride ?? getHmacSecret();
  if (!secret) return undefined;

  const timestamp = Date.now().toString();
  const payload = `${timestamp}.${body}`;

  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode(secret),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );
    const signatureBuffer = await crypto.subtle.sign(
      'HMAC',
      key,
      encoder.encode(payload),
    );
    const signature = Array.from(new Uint8Array(signatureBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return `${timestamp}:${signature}`;
  }

  // React Native / Node.js fallback using crypto-js
  try {
    const signature = CryptoJS.HmacSHA256(payload, secret).toString(CryptoJS.enc.Hex);
    return `${timestamp}:${signature}`;
  } catch {
    // Final fallback: Node.js crypto
    try {
      const { createHmac } = await import('crypto');
      const signature = createHmac('sha256', secret)
        .update(payload)
        .digest('hex');
      return `${timestamp}:${signature}`;
    } catch {
      return undefined;
    }
  }
};

export const createSkinSeoulAnalyticsClient = (
  _baseURL?: string,
  _hmacSecret?: string,
): AxiosInstance => {
  const initialBaseURL = _baseURL ?? getEventBaseURL();

  const instance = axios.create({
    baseURL: initialBaseURL,
    withCredentials: true,
    adapter: 'fetch',
  });

  instance.interceptors.request.use(async (config) => {
    // Dynamic baseURL update (runtime env may differ from build-time)
    if (typeof window !== 'undefined') {
      const currentBaseURL = getEventBaseURL();
      const configBaseURL = config.baseURL || instance.defaults.baseURL;

      if (
        currentBaseURL !== DEFAULT_EVENT_URL &&
        configBaseURL !== currentBaseURL
      ) {
        config.baseURL = currentBaseURL;
      }
    }

    // HMAC signature
    if (config.data) {
      const bodyJson =
        typeof config.data === 'string'
          ? config.data
          : JSON.stringify(config.data);

      const signature = await signRequest(bodyJson, _hmacSecret);
      if (signature) {
        config.headers['X-Analytics-Signature'] = signature;
      }
    }

    return config;
  });

  attachServerLoggingInterceptor(instance, 'analyticsClient');

  return instance;
};

export let analyticsClient: AxiosInstance = createSkinSeoulAnalyticsClient();

export const setAnalyticsClient = (instance: AxiosInstance) => {
  analyticsClient = instance;
};
